import i18n from "@/modules/i18n";
import enabledPaymentMethods from "@/utils/payment-methods";

export default {
  productName: (state) => {
    if (state.productType === "giftcards") {
      return i18n.t("checkout.giftcards.name");
    }
    if (state.productType === "credits") {
      return i18n.t(`checkout.bundles.${state.plan.sku}`);
    }
    if (state.productType === "subscriptions") {
      return i18n.t("checkout.subscriptions.product", {
        period: i18n.t(`checkout.subscriptions.period.${state.plan.type}`),
        name: i18n.t("checkout.subscriptions.name")
      });
    }
    if (state.productType === "teams") {
      return i18n.t("checkout.teams.product", {
        teamSize: state.plan.teamSize
      });
    }
    if (state.plan.name) {
      return state.plan.name;
    }
    return state.productType;
  },
  spelledCurrency: (state) => {
    return i18n.t(`checkout.currencies.${state.plan.currency.toLowerCase()}`);
  },
  disclaimer: (_, getters) => {
    return i18n.t("checkout.disclaimer.label", { currency: getters.spelledCurrency });
  },
  paymentMethods: (state) => {
    return state.plan.currency && enabledPaymentMethods(state.plan.currency, state.countryCode);
  },
  showBilledAfterTrial: () => {
    return false;
  },
  hasTrial: (state) => {
    return !!state.plan.trialDays;
  },
  couponAvailable: (state) => {
    return ((state.productType === "subscriptions") || (state.productType === "teams"));
  },
  hasCoupon: (state) => {
    return !!state.coupon.type;
  },
  couponDiscount: (state) => {
    return state.coupon.type === "percent" && state.coupon.value;
  },
  hasTax: (state) => {
    return !!state.tax.amount;
  },
  taxRegion: (state) => {
    return state.tax.region;
  },
  taxAmount: (state) => {
    return state.tax.amount;
  },
  couponCredits: (state) => {
    return state.coupon.type === "credit" && state.coupon.value;
  },
  couponTrialDays: (state) => {
    return state.coupon.type === "free_trial" && state.coupon.value;
  },
  twoForOne: (state) => {
    return state.coupon.type === "two_for_one";
  },
  trialDays: (state, getters) =>  {
    if (!getters.hasTrial) {
      return;
    }
    return state.plan.trialDays;
  },
  teamOwnerPrice: (state) => {
    if (state.productType !== "teams") {
      return undefined;
    }
    if (state.plan.currency === "eur") {
      return "0,00€";
    } else {
      return "$0.00";
    }
  },
  aboDuration: (state, getters) => {
    if (!getters.hasTrial) {
      return;
    }
    return i18n.t(`checkout.subscriptions.aboDuration.${state.plan.duration}`);
  },
  isInVariation: (state) => {
    return state.isInVariation;
  },
  summaryDescriptionI18nKey: (state, getters) => {
    if (getters.isInVariation) {
      return "variation";
    }

    if (getters.hasTrial) {
      return "trial";
    }
    if (state.coupon.type === "credits") {
      return "credits";
    }

    return "default";
  },
  submitI18nKey: (state) => {
    if (state.sending) {
      return "sending";
    }
    if (state.productType === "credits") {
      return "credits";
    }
    if (state.isInVariation) {
      return "variation";
    }
    if (state.plan.trialDays) {
      return "trial";
    }
    if (["subscriptions", "teams"].includes(state.productType)) {
      return "subscribe";
    }
    return "default";
  },
  disclaimerI18nKey: (state) => {
    if (state.productType === "credits") {
      return "credits";
    }
    if (state.plan.price.discount && state.plan.trialDays) {
      return "trialDiscount";
    }
    if (state.isInVariation) {
      return "variation";
    }
    if (state.plan.trialDays) {
      return "trial";
    }
    if (state.productType === "subscriptions") {
      return "subscribe";
    }
    if (state.productType === "teams") {
      return "teams";
    }
    if (state.productType === "giftcards") {
      return "giftcards";
    }
    return "default";
  },
  trialEndDate: (state) => {
    const date = new Date();
    date.setDate(date.getDate() + state.plan.trialDays);
    return date.toLocaleDateString("fr-CA"); // YYYY-MM-DD
  },
  trialLastDay: (state) => {
    const date = new Date();
    date.setDate(date.getDate() + state.plan.trialDays - 1);
    return date.toLocaleDateString("fr-CA"); // YYYY-MM-DD
  },
  originalPrice: (state, getters) => {
    if (state.plan.price.original && state.plan.price.original.markup) {
      return state.plan.price.original.markup;
    }
    return getters["finalPrice"];
  },
  finalPrice: (state) => {
    if (state.plan.price.final && state.plan.price.final.markup) {
      return state.plan.price.final.markup;
    }
    return "-";
  },
  freeMarkup: (state) => {
    if (state.plan.freeMarkup) {
      return state.plan.freeMarkup;
    }
    return "-";
  },
  discountAmount: (state) => {
    if (state.plan.price.discount && state.plan.price.discount.markup) {
      return state.plan.price.discount.markup;
    }
    return "-";
  },
  formattedPrice: (state) => {
    return state.plan && state.plan.price.final.markup;
  },
  isCountryUS: (state) => {
    return state.countryCode === "us";
  },
  countryCode: (state) => {
    return state.countryCode;
  },
  showGiftcardFields: (state) => {
    return state.productType === "giftcards";
  },
  giftcardParams: (state) => {
    return state.giftcard;
  }
};
